import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from "../config/api";
import SearchableDropdown from "./SearchableDropdown";
import "./PreMidtermAnalysis.css";

const SITE_LABELS = {
  wikipedia_tr: "Vikipedi (TR)", acikders: "Açık Ders", udemy_tr: "Udemy (Türkçe)",
  khanacademy: "Khan Academy TR", wikipedia: "Vikipedi", mit_ocw: "MIT OpenCourseWare",
  coursera: "Coursera", udemy: "Udemy", edx: "edX", brilliant: "Brilliant",
  openstax: "OpenStax", libretexts: "LibreTexts", wikiversity: "Wikiversity",
  geeksforgeeks: "GeeksforGeeks", w3schools: "W3Schools", tutorialspoint: "Tutorialspoint",
  programiz: "Programiz", freecodecamp: "freeCodeCamp", stackoverflow: "Stack Overflow",
  youtube: "YouTube", google: "Google",
};

const SITE_DOMAINS = {
  wikipedia_tr: "tr.wikipedia.org", acikders: "acikders.org.tr", udemy_tr: "udemy.com",
  khanacademy: "tr.khanacademy.org", wikipedia: "en.wikipedia.org", mit_ocw: "ocw.mit.edu",
  coursera: "coursera.org", udemy: "udemy.com", edx: "edx.org", brilliant: "brilliant.org",
  openstax: "openstax.org", libretexts: "libretexts.org", wikiversity: "en.wikiversity.org",
  geeksforgeeks: "geeksforgeeks.org", w3schools: "w3schools.com", tutorialspoint: "tutorialspoint.com",
  programiz: "programiz.com", freecodecamp: "freecodecamp.org", stackoverflow: "stackoverflow.com",
};

function buildWebUrl(site_key, q) {
  const eq = encodeURIComponent(q);
  if (site_key === "wikipedia_tr") return `https://tr.wikipedia.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "wikipedia") return `https://en.wikipedia.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "wikiversity") return `https://en.wikiversity.org/wiki/Special:Search?search=${eq}`;
  if (site_key === "udemy_tr") return `https://www.udemy.com/courses/search/?q=${eq}&lang=tr`;
  if (site_key === "udemy") return `https://www.udemy.com/courses/search/?q=${eq}`;
  if (site_key === "coursera") return `https://www.coursera.org/search?query=${eq}`;
  if (site_key === "stackoverflow") return `https://stackoverflow.com/search?q=${eq}`;
  if (site_key === "youtube") return `https://www.youtube.com/results?search_query=${eq}`;
  if (site_key === "khanacademy") return `https://tr.khanacademy.org/search?page_search_query=${eq}`;
  if (site_key === "mit_ocw") return `https://ocw.mit.edu/search/?q=${eq}`;
  if (site_key === "freecodecamp") return `https://www.freecodecamp.org/news/search/?query=${eq}`;
  if (site_key === "geeksforgeeks") return `https://www.geeksforgeeks.org/search/?q=${eq}`;
  const domain = SITE_DOMAINS[site_key];
  if (domain) return `https://www.google.com/search?q=site:${domain}+${eq}`;
  return `https://www.google.com/search?q=${eq}`;
}

const TYPE_ICONS = { tutorial: "📖", article: "📄", documentation: "📋", interactive: "🎮" };
function typeIcon(t) { return TYPE_ICONS[t] || "🔗"; }

function AiResourceCards({ analysis, ytMap }) {
  const videos = analysis?.videos || [];
  const webRes = analysis?.web_resources || [];
  if (!videos.length && !webRes.length) return null;

  const openUrl = (url) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="pm-ai-cards">
      {videos.map((v, i) => {
        const yt = ytMap[v.search_query];
        const url = yt?.video_url
          || `https://www.youtube.com/results?search_query=${encodeURIComponent(v.search_query || v.title || "")}`;
        const title = yt?.title || v.title || "YouTube";
        const thumb = yt?.thumbnail;
        const dur = yt?.duration_sec
          ? `${Math.round(yt.duration_sec / 60)} dk`
          : v.duration_min ? `${v.duration_min} dk` : null;
        return (
          <a key={`yt-${i}`} href={url} target="_blank" rel="noopener noreferrer"
             className="pm-ai-video-card" onClick={openUrl(url)}>
            <div className="pm-ai-thumb">
              {thumb ? <img src={thumb} alt="" loading="lazy" /> : <span className="pm-ai-play">▶</span>}
            </div>
            <div className="pm-ai-info">
              <span className="pm-ai-title">{title.slice(0, 52)}{title.length > 52 ? "…" : ""}</span>
              <span className="pm-ai-meta">{yt?.channel || v.channel || "YouTube"}{dur && ` · ${dur}`}</span>
            </div>
          </a>
        );
      })}
      {webRes.map((r, i) => {
        const url = buildWebUrl(r.site_key, r.title || r.description || "");
        return (
          <a key={`web-${i}`} href={url} target="_blank" rel="noopener noreferrer"
             className="pm-ai-web-card" onClick={openUrl(url)}>
            <span className="pm-ai-web-icon">{typeIcon(r.type)}</span>
            <div className="pm-ai-info">
              <span className="pm-ai-title">
                {(r.title || SITE_LABELS[r.site_key] || r.site_key || "Web").slice(0, 40)}
              </span>
              <span className="pm-ai-meta">{SITE_LABELS[r.site_key] || r.site_key}</span>
            </div>
          </a>
        );
      })}
    </div>
  );
}

/**
 * Akademisyen için ağırlıklı ÖÇ/PÖÇ analiz ekranı.
 * - Arasınav Öncesi: quiz/ödev vb. (vize/final/bütünleme hariç)
 * - Arasınav Sonrası: vize dahil (final/bütünleme hariç)
 */
function pctColor(pct) {
  if (pct === null || pct === undefined) return "#e5e7eb";
  const v = Math.max(0, Math.min(100, pct));
  const hue = (v / 100) * 120;
  return `hsl(${hue}, 70%, 55%)`;
}

function labelForExamType(t) {
  switch (t) {
    case "vize": return "Ara Sınav";
    case "final": return "Final";
    case "but":
    case "bütünleme": return "Bütünleme";
    case "quiz": return "Kısa Sınav";
    case "odev":
    case "ödev": return "Ödev";
    case "pretest": return "Ön Test";
    case "pro": return "Proje";
    default: return t.charAt(0).toUpperCase() + t.slice(1);
  }
}

function PctCell({ value }) {
  const display = value === null || value === undefined ? "—" : value.toFixed(1);
  return (
    <span className="pm-pct-pill" style={{ background: pctColor(value) }}>
      {display}
    </span>
  );
}

function AnalysisPanel({ courseId, mode, courseName }) {
  const [classData, setClassData] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const detailRef = useRef(null);

  const [aiResultMap, setAiResultMap] = useState({});
  const [ytMap, setYtMap] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  useEffect(() => {
    if (selectedStudent && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedStudent]);

  useEffect(() => {
    setAiResultMap({});
    setYtMap({});
    setAiError("");
  }, [selectedStudent?.student_no]);

  const handleLoadAi = async () => {
    if (!selectedStudent || aiLoading || !courseName) return;
    const weakOc = (selectedStudent.learning_outcomes || []).filter(o => (o.pct ?? 999) < 65);
    if (!weakOc.length) return;
    setAiLoading(true);
    setAiError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/student-outcome-resources`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          course_id: courseId,
          course_name: courseName,
          outcomes: weakOc.map(o => ({
            code: o.code, description: o.description, pct: o.pct,
            exam_evidence: o.exam_evidence || [],
          })),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "AI hatası");
      const aMap = {};
      (data.analyses || []).forEach(a => { aMap[a.outcome_code] = a; });
      setAiResultMap(aMap);

      const queries = [...new Set(
        (data.analyses || []).flatMap(a => (a.videos || []).map(v => v.search_query).filter(Boolean))
      )];
      if (queries.length) {
        const ytResults = await Promise.all(
          queries.map(q =>
            fetch(`${API_BASE_URL}/api/youtube/search?q=${encodeURIComponent(q)}&max=1`, { credentials: "include" })
              .then(r => r.json())
              .then(d => ({ q, video: (d.videos || [])[0] || null }))
              .catch(() => ({ q, video: null }))
          )
        );
        const yMap = {};
        ytResults.forEach(({ q, video }) => { yMap[q] = video; });
        setYtMap(yMap);
      }
    } catch (e) {
      setAiError(`AI kaynakları yüklenemedi: ${e.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (!courseId) {
      setClassData(null);
      setSelectedStudent(null);
      return;
    }
    setLoading(true);
    setError("");
    const endpoint = mode === "post"
      ? `/api/pre-midterm/post-midterm-class-breakdown?course_id=${courseId}`
      : `/api/pre-midterm/class-breakdown?course_id=${courseId}`;
    fetch(`${API_BASE_URL}${endpoint}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setClassData(data);
        setSelectedStudent(null);
      })
      .catch((e) => setError(`Analiz alınamadı: ${e.message}`))
      .finally(() => setLoading(false));
  }, [courseId, mode]);

  const overallKey = mode === "post" ? "overall_post_midterm_pct" : "overall_pre_midterm_pct";

  const examTypes = useMemo(() => {
    if (!classData) return [];
    return Object.entries(classData.exam_type_weights || {}).sort((a, b) => b[1] - a[1]);
  }, [classData]);

  const student = selectedStudent;

  if (loading) return <span className="pm-loading">Yükleniyor…</span>;

  return (
    <div>
      {error && <div className="pm-error">{error}</div>}

      {classData && classData.note && (
        <div className="pm-warn">{classData.note}</div>
      )}

      {classData && examTypes.length > 0 && (
        <section className="pm-section">
          <h3>Sınav Türü Ağırlıkları (normalize)</h3>
          <div className="pm-weights">
            {examTypes.map(([t, w]) => (
              <div key={t} className="pm-weight-chip">
                <strong>{labelForExamType(t)}</strong> — %{Math.round(w * 100)}
              </div>
            ))}
          </div>
          {classData.class_averages?.[overallKey] !== null &&
            classData.class_averages?.[overallKey] !== undefined && (
              <p className="pm-overall">
                {mode === "post"
                  ? "Sınıf genel arasınav sonrası başarı:"
                  : "Sınıf genel arasınav öncesi başarı:"}{" "}
                <PctCell value={classData.class_averages[overallKey]} />
              </p>
            )}
        </section>
      )}

      {classData && classData.students && classData.students.length > 0 && (
        <section className="pm-section">
          <h3>Öğrenci Listesi ({classData.students.length})</h3>
          <table className="pm-table">
            <thead>
              <tr>
                <th>Numara</th>
                <th>Ad Soyad</th>
                <th>Genel Puan</th>
                <th>ÖÇ sayısı</th>
                <th>PÖÇ sayısı</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {classData.students.map((s) => (
                <tr
                  key={s.student_no}
                  className={
                    selectedStudent?.student_no === s.student_no ? "pm-row-active" : ""
                  }
                >
                  <td>{s.student_no}</td>
                  <td>{s.student_name || "—"}</td>
                  <td><PctCell value={s[overallKey]} /></td>
                  <td>{s.learning_outcomes?.length || 0}</td>
                  <td>{s.program_outcomes?.length || 0}</td>
                  <td>
                    <button className="pm-btn" onClick={() => setSelectedStudent(s)}>
                      Detay
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {student && (
        <section className="pm-section pm-detail" ref={detailRef}>
          {(!student.learning_outcomes?.length && !student.program_outcomes?.length) && (
            <div className="pm-warn">
              Bu öğrenci için ÖÇ/PÖÇ verisi yok
              {student.note ? ` — ${student.note}` : ""}.
            </div>
          )}
          <h3>
            Öğrenci Detayı — {student.student_name || student.student_no}
            <span className="pm-h3-btns">
              {(student.learning_outcomes || []).some(o => (o.pct ?? 999) < 65) && (
                <button className="pm-btn pm-btn-ai" onClick={handleLoadAi} disabled={aiLoading}>
                  {aiLoading ? "Yükleniyor…" : Object.keys(aiResultMap).length > 0 ? "Yenile" : "AI Kaynakları"}
                </button>
              )}
              <button className="pm-btn pm-btn-light" onClick={() => setSelectedStudent(null)}>
                Kapat
              </button>
            </span>
          </h3>
          {aiError && <div className="pm-error">{aiError}</div>}

          <div className="pm-grid">
            <div>
              <h4>Sınav Türü Kırılımı</h4>
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Tür</th>
                    <th>Ağırlık</th>
                    <th>Puan</th>
                  </tr>
                </thead>
                <tbody>
                  {(student.exam_type_breakdown || []).map((b) => (
                    <tr key={b.exam_type}>
                      <td>{labelForExamType(b.exam_type)}</td>
                      <td>%{Math.round((b.weight_norm || 0) * 100)}</td>
                      <td><PctCell value={b.score_pct} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="pm-overall">
                Genel:{" "}
                <PctCell value={student[overallKey]} />
              </p>
            </div>

            <div>
              <h4>Öğrenme Çıktıları (ÖÇ)</h4>
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Kod</th>
                    <th>Açıklama</th>
                    <th>Puan</th>
                    <th>Katkı veren türler</th>
                  </tr>
                </thead>
                <tbody>
                  {(student.learning_outcomes || []).map((o) => (
                    <React.Fragment key={o.id}>
                      <tr>
                        <td>{o.code}</td>
                        <td className="pm-desc">{o.description}</td>
                        <td><PctCell value={o.pct} /></td>
                        <td className="pm-muted">
                          {(o.contributing_exam_types || []).map(labelForExamType).join(", ")}
                        </td>
                      </tr>
                      {aiResultMap[o.code] && (
                        <tr>
                          <td colSpan={4} className="pm-ai-resources-row">
                            <AiResourceCards analysis={aiResultMap[o.code]} ytMap={ytMap} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h4>Program Öğrenme Çıktıları (PÖÇ)</h4>
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Kod</th>
                    <th>Açıklama</th>
                    <th>Puan</th>
                    <th>Katkı veren türler</th>
                  </tr>
                </thead>
                <tbody>
                  {(student.program_outcomes || []).map((o) => (
                    <tr key={o.id}>
                      <td>{o.code}</td>
                      <td className="pm-desc">{o.description}</td>
                      <td><PctCell value={o.pct} /></td>
                      <td className="pm-muted">
                        {(o.contributing_exam_types || []).map(labelForExamType).join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {classData && classData.class_averages?.learning_outcomes?.length > 0 && (
        <section className="pm-section">
          <h3>Sınıf Ortalaması — ÖÇ</h3>
          <table className="pm-table">
            <thead>
              <tr>
                <th>Kod</th>
                <th>Açıklama</th>
                <th>Ortalama Puan</th>
                <th>Öğrenci sayısı</th>
              </tr>
            </thead>
            <tbody>
              {classData.class_averages.learning_outcomes.map((o) => (
                <tr key={o.id}>
                  <td>{o.code}</td>
                  <td className="pm-desc">{o.description}</td>
                  <td><PctCell value={o.avg_pct} /></td>
                  <td>{o.student_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {classData && classData.class_averages?.program_outcomes?.length > 0 && (
        <section className="pm-section">
          <h3>Sınıf Ortalaması — PÖÇ</h3>
          <table className="pm-table">
            <thead>
              <tr>
                <th>Kod</th>
                <th>Açıklama</th>
                <th>Ortalama Puan</th>
                <th>Öğrenci sayısı</th>
              </tr>
            </thead>
            <tbody>
              {classData.class_averages.program_outcomes.map((o) => (
                <tr key={o.id}>
                  <td>{o.code}</td>
                  <td className="pm-desc">{o.description}</td>
                  <td><PctCell value={o.avg_pct} /></td>
                  <td>{o.student_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

export default function PreMidtermAnalysis() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");

  const courseName = useMemo(() => {
    const c = courses.find(x => String(x.id) === String(courseId));
    return c ? `${c.code} — ${c.name}` : "";
  }, [courses, courseId]);

  useEffect(() => {
    const akademisyenId = sessionStorage.getItem("akademisyen_id");
    if (!akademisyenId) return;
    fetch(`${API_BASE_URL}/api/akademisyen/courses?akademisyen_id=${akademisyenId}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => setCourses(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  return (
    <div className="pm-wrap">
      <h2 className="pm-title">Öğrenci ÖÇ/PÖÇ Analizi</h2>

      <div className="pm-controls">
        <label>Ders:</label>
        <SearchableDropdown
          value={courseId}
          onChange={setCourseId}
          placeholder="— Ders seçin —"
          searchPlaceholder="Ders adı veya koduyla ara…"
          ariaLabel="Ders"
          options={courses.map(c => ({
            value: c.id,
            label: `${c.code} — ${c.name}`,
            searchText: `${c.code || ''} ${c.name || ''}`,
            badge: c.has_midterm_analysis ? "Ara Sınav Analizi Hazır" : "",
          }))}
        />
      </div>

      <AnalysisPanel key={courseId} courseId={courseId} mode="post" courseName={courseName} />
    </div>
  );
}
